import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { NotificationsProvider } from "@/lib/notifications";
import { UpcomingRideBanner } from "@/components/UpcomingRideBanner";
import { InstallPrompt } from "@/components/InstallPrompt";
import "@/i18n";

import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center fade-in">
        <h1 className="font-display text-8xl text-primary">404</h1>
        <h2 className="mt-2 text-xl text-foreground">Ruta no encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Esta página no existe o ha sido movida.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground"
        >
          Volver al mapa
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl text-foreground">Algo se ha torcido</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <button
          onClick={() => { router.invalidate(); reset(); }}
          className="mt-6 inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground"
        >
          Reintentar
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#0f0f0f" },
      { title: "Watts — Queda. Pedalea. Comparte." },
      { name: "description", content: "Watts conecta a ciclistas en tiempo real. Encuentra salidas cerca de ti y únete en un toque." },
      { property: "og:title", content: "Watts — Queda. Pedalea. Comparte." },
      { property: "og:description", content: "Watts conecta a ciclistas en tiempo real. Encuentra salidas cerca de ti y únete en un toque." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Watts — Queda. Pedalea. Comparte." },
      { name: "twitter:description", content: "Watts conecta a ciclistas en tiempo real. Encuentra salidas cerca de ti y únete en un toque." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/53110582-9755-43ad-9818-ce9c9637adcf/id-preview-615593ff--098511e5-287a-4832-98f3-682b8458a5e4.lovable.app-1779375564580.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/53110582-9755-43ad-9818-ce9c9637adcf/id-preview-615593ff--098511e5-287a-4832-98f3-682b8458a5e4.lovable.app-1779375564580.png" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap" },
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    Sentry.init({
      dsn: "https://f71f8b5da9258fbfc05b01ae7cd85b5f@o4511514096500736.ingest.de.sentry.io/4511514131169360",
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NotificationsProvider>
          <UpcomingRideBanner />
          <Outlet />
          <InstallPrompt />
          <Toaster
            theme="dark"
            position="bottom-center"
            duration={2500}
            toastOptions={{
              classNames: {
                toast: "!bg-[#1a1a1a] !text-foreground !border-l-4 !border-l-primary !border-y-border !border-r-border font-sans !rounded-xl",
                success: "!border-l-[#4caf7d]",
                error: "!border-l-[#ef4444] !text-[#fca5a5]",
                description: "!text-muted-foreground",
              },
            }}
          />
        </NotificationsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
